import { useEffect, useRef, useState } from 'react';

import { anzeigeZahlen, plakettenText, standAus } from '../../domain/rueckstand';
import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import { Icon } from '../../ui/Icon';

import styles from './Rueckstandspille.module.css';

/**
 * Was noch hinaus muss — in der Kopfzeile, immer sichtbar.
 *
 * ⚠️ **Der Anlass** (Vorgabe des Nutzers, 2026-09-22): in vier von sechs Ausgängen einer
 * Übertragung merkt der Prüfer heute **nichts**. Kein Netz, ein abgelehnter Eintrag, ein
 * unbekannter Typ — er bewertet weiter, und die Zahl der wartenden Belege steht nur im
 * Einstellungsblatt, an das er im geführten Zugriff gar nicht herankommt.
 *
 * ⚠️ **Ein Tipp öffnet einen Aufklapper, kein Blatt.** „Jetzt übertragen" liegt hinter der
 * Passwortsperre der Einstellungen (`app/App.tsx`); ein Knopf, der dorthin führt, endete für
 * den Prüfer an einer Tür ohne Schlüssel. Der Aufklapper sagt ihm stattdessen, **was** liegt
 * und **woran** es hängt — damit kann er die Prüfungsleitung rufen, und das ist während einer
 * laufenden Station ohnehin der richtige Weg.
 *
 * ⚠️ **Beide Gleise zeigen beide Zahlen.** Auch im Classic-Modus gehen Aufnahmen hinaus,
 * sobald sie anfallen (Richtigstellung des Nutzers, 2026-09-22); welche Liste gezählt wird,
 * entscheidet `server/rueckstand.ts` je Gleis — die Anzeige muss davon nichts wissen.
 *
 * ⭐ **Seit dem 2026-09-26 zwei Zeichen mit Kreis statt einer Pille** (Vorgabe des Nutzers).
 * Die Zahlen standen als Ziffern daneben und machten die Anzeige rund 110 px breit; der Kreis
 * darüber — das Muster vom iPhone — kostet die halbe Breite. In der Kopfzeile des iPad Air 2
 * ist das der Unterschied zwischen einem abgeschnittenen und einem lesbaren Stationsnamen.
 *
 * ⚠️ **Es bleibt *ein* Knopf**, nicht zwei. Ein Tippziel, ein Aufklapper, eine Beschriftung für
 * Vorlesehilfen. Zwei Knöpfe nebeneinander wären auf dem Gerät zwei Ziele von je 17 px — für
 * einen Finger zu wenig, und für den Prüfer wäre nicht ersichtlich, dass beide dasselbe
 * öffnen.
 *
 * ⚠️ **Kein Rot** (Vorgabe des Nutzers vom 2026-09-22, am 2026-09-26 an der Attrappe bestätigt).
 * Der Kreis trägt den Zustandston, den bis dahin die ganze Pille trug — bernstein beim Warten
 * **und** beim Stecken. Der Unterschied zwischen beiden steht im Aufklapper, wo „aufgegeben"
 * mitzählt; in der Kopfzeile wäre ein Alarmzeichen für etwas, woran während einer laufenden
 * Station nichts zu tun ist.
 */
export function Rueckstandspille() {
  const t = useT();
  const zahlen = useAppStore((s) => s.rueckstand);
  const sendet = useAppStore((s) => s.rueckstandSendet);
  const meldung = useAppStore((s) => s.rueckstandMeldung);
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);

  const stand = standAus(zahlen, sendet);
  const { pakete, dateien } = anzeigeZahlen(zahlen);

  /*
   * Ein Tipp daneben schliesst wieder — und die Escape-Taste, für den Fall, dass jemand mit
   * Tastatur arbeitet. Ohne beides bliebe der Kasten über dem Bogen stehen.
   */
  useEffect(() => {
    if (!offen) return;
    const daneben = (e: PointerEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false);
    };
    document.addEventListener('pointerdown', daneben);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('pointerdown', daneben);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  // ⚠️ Die Beschriftung bekommt die **vollen** Zahlen, nicht die gekappten: „99+ Pakete"
  // vorgelesen zu bekommen wäre schlechter als die Zahl, und im Kreis ist der Platz die
  // einzige Begründung fürs Kappen.
  const beschriftung = t.backlog.label(pakete, dateien);
  const paketePlakette = plakettenText(pakete);
  const dateienPlakette = plakettenText(dateien);

  return (
    <div className={styles.huelle} ref={huelle}>
      <button
        type="button"
        className={`tap ${styles.anzeige}`}
        data-stand={stand}
        onClick={() => setOffen((war) => !war)}
        aria-expanded={offen}
        aria-label={beschriftung}
        title={beschriftung}
      >
        {/*
          Das Zeichen trägt seinen Kreis, nicht umgekehrt: `position: relative` sitzt auf der
          Hülle, damit die Plakette über der oberen rechten Ecke des Zeichens hängt, wo sie
          jeder erwartet — und damit sie mitwandert, wenn die Zeichengrösse sich je ändert.
        */}
        <span className={`${styles.zeichen} ${styles.senden}`}>
          <Icon name="send" size={17} />
          {paketePlakette && <span className={styles.plakette}>{paketePlakette}</span>}
        </span>
        <span className={styles.zeichen}>
          <Icon name="doc" size={17} />
          {dateienPlakette && <span className={styles.plakette}>{dateienPlakette}</span>}
        </span>
      </button>

      {offen && (
        <div className={styles.aufklapper} role="dialog" aria-label={t.backlog.title}>
          <div className={styles.zeile}>
            <span>{t.backlog.packets}</span>
            <b>{pakete}</b>
          </div>
          <div className={styles.zeile}>
            <span>{t.backlog.files}</span>
            <b>{dateien}</b>
          </div>
          {/*
            Abgewiesene stehen eigens und in Warnfarbe: sie warten auf nichts mehr. Die Datei
            liegt weiter auf dem iPad, nur der Weg zum Server ist zu — wer das nicht sieht,
            hält sie für übertragen.
          */}
          {zahlen.abgewiesen > 0 && (
            <div className={`${styles.zeile} ${styles.warnung}`}>
              <span>{t.backlog.rejected}</span>
              <b>{zahlen.abgewiesen}</b>
            </div>
          )}

          <div className={styles.stand}>{t.backlog.state[stand]}</div>

          {meldung && (
            <div className={styles.meldung}>
              <span className={styles.zeit}>{meldung.zeit}</span> · {meldung.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
